import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Body, Query } from '../op-types';

export function createNewsQueries(client: StoxlyClient) {
  return createQueryKeys('news', {
    // ['news', 'articles', { query }]
    articles: (query?: Query<'newsGetArticles'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.newsGetArticles({ queries: query, signal }),
    }),

    // ['news', 'article', id]
    article: (id: string) => ({
      queryKey: [id],
      queryFn: ({ signal }) => client.newsGetArticle({ params: { id }, signal }),
    }),

    // ['news', 'globalNews', { query }]
    globalNews: (query?: Query<'newsGetGlobalNews'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.newsGetGlobalNews({ queries: query, signal }),
    }),

    // ['news', 'globalNewsArticle', id]
    globalNewsArticle: (id: string) => ({
      queryKey: [id],
      queryFn: ({ signal }) => client.newsGetGlobalNewsArticle({ params: { id }, signal }),
    }),

    // ['news', 'marketContexts', { query }]
    marketContexts: (query: Query<'newsGetMarketContexts'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.newsGetMarketContexts({ queries: query, signal }),
    }),

    // ['news', 'globalEvents', { query }]
    //
    // The world's living stories. The list is read as an INFINITE query by its consumer, which
    // this shape cannot express on its own — a page's cursor rides in `query`, so the caller
    // passes `{ ...filters, cursor: pageParam }` per page and appends its own `'infinite'`
    // segment. What lives here is what must not be re-derived per app: the operation, the
    // parameter mapping and the key ROOT they all hang off.
    globalEvents: (query?: Query<'newsListGlobalEvents'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.newsListGlobalEvents({ queries: query, signal }),
    }),

    // ['news', 'globalEvent', id, ...]
    //
    // Keyed by the id ASKED FOR. A merged event resolves to the story that swallowed it, so two
    // ids can legitimately answer with one payload; keying by the response's id would make a
    // merged link a permanent cache miss.
    globalEvent: (id: string) => ({
      queryKey: [id],
      queryFn: ({ signal }) => client.newsGetGlobalEvent({ params: { id }, signal }),
      contextQueries: {
        // ['news', 'globalEvent', id, 'development', developmentId]
        development: (developmentId: string) => ({
          queryKey: [developmentId],
          queryFn: ({ signal }) =>
            client.newsGetGlobalEventDevelopment({ params: { id, developmentId }, signal }),
        }),
      },
    }),

    // ['news', 'timelineEvent', id]
    timelineEvent: (id: string) => ({
      queryKey: [id],
      queryFn: ({ signal }) => client.newsGetTimelineEvent({ params: { id }, signal }),
    }),

    // ['news', 'livestreamChannels']
    livestreamChannels: {
      queryKey: null,
      queryFn: ({ signal }) => client.livestreamsGetChannels({ signal }),
    },

    // ['news', 'livestreamLiveCheck', { query }]
    livestreamLiveCheck: (query: Query<'livestreamsCheckLive'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.livestreamsCheckLive({ queries: query, signal }),
    }),

    // ['news', 'trackedTickers']
    trackedTickers: {
      queryKey: null,
      queryFn: ({ signal }) => client.trackedTickersGetTrackedTickers({ signal }),
    },

    // ['news', 'planSlotNotice']
    //
    // IS THE PLAN-COVERAGE DIALOG OWED TO THIS READER (#918). Its own operation rather than a
    // field on `trackedTickers` above: that response enriches every row with upstream stats and
    // only the news screens ask for it, while this question is owed anywhere in the app — the
    // reader it exists for is the one who logs in and goes straight to their portfolios.
    planSlotNotice: {
      queryKey: null,
      queryFn: ({ signal }) => client.trackedTickersGetPlanSlotNotice({ signal }),
    },

    // ['news', 'trackedDailySummaries', { query }]
    //
    // THE WHOLE TRACKED SET's days in one request — what the news wall reads. It names no ticker:
    // the server resolves the scope from the caller's own tracked list, the way the article feed
    // does. Per symbol this was 27 requests for a full set, and another 27 for every step back
    // through the history.
    trackedDailySummaries: (query?: Query<'newsListTrackedDailyInsights'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.newsListTrackedDailyInsights({ queries: query, signal }),
    }),

    // ['news', 'ticker', ticker, ...]
    ticker: (ticker: string) => ({
      queryKey: [ticker],
      contextQueries: {
        tickerStats: {
          queryKey: null,
          queryFn: ({ signal }) => client.newsGetTickerStats({ params: { ticker }, signal }),
        },
        timeline: (query?: Query<'newsGetTimeline'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.newsGetTimeline({ params: { ticker }, queries: query, signal }),
        }),
        dailyInsight: (date: string) => ({
          queryKey: [date],
          queryFn: ({ signal }) => client.newsGetDailyInsight({ params: { ticker, date }, signal }),
        }),
        // A RANGE of days for one symbol. The single-day read above cannot serve it: its day is a
        // path parameter with no "latest" form, so walking backwards would be one request per day
        // per symbol, against days that may not exist.
        dailyInsights: (query?: Query<'newsListDailyInsights'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.newsListDailyInsights({ params: { ticker }, queries: query, signal }),
        }),
        weeklyInsights: (query: Query<'newsGetWeeklyInsights'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.newsGetWeeklyInsights({ params: { ticker }, queries: query, signal }),
        }),
        monthlyInsights: (query: Query<'newsGetMonthlyInsights'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.newsGetMonthlyInsights({ params: { ticker }, queries: query, signal }),
        }),
        overallInsight: {
          queryKey: null,
          queryFn: ({ signal }) => client.newsGetOverallInsight({ params: { ticker }, signal }),
        },
        // fox F1: 24h volume pulse + trailing-week sentiment distribution/mood
        pulse: {
          queryKey: null,
          queryFn: ({ signal }) => client.newsGetTickerPulse({ params: { ticker }, signal }),
        },
        // fox F1: archived versions of the OVERALL narrative, newest first
        narrativeHistory: (query?: Query<'newsGetNarrativeHistory'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.newsGetNarrativeHistory({ params: { ticker }, queries: query, signal }),
        }),
      },
    }),
  });
}

export function createNewsMutations(client: StoxlyClient) {
  return {
    addTrackedTicker: {
      mutationFn: (body: Body<'trackedTickersAddTrackedTicker'>) =>
        client.trackedTickersAddTrackedTicker(body),
    },
    removeTrackedTicker: {
      mutationFn: (symbol: string) =>
        client.trackedTickersRemoveTrackedTicker(undefined, { params: { symbol } }),
    },
    // WHICH COMPANIES OCCUPY THE PLAN SLOTS (#918). The body is the WHOLE active set, not one
    // symbol: the dialog saves on every press with no button to debounce, so an idempotent PUT
    // is what keeps a double-tap from meaning something.
    setActiveTickers: {
      mutationFn: (body: Body<'trackedTickersSetActiveTickers'>) =>
        client.trackedTickersSetActiveTickers(body),
    },
    // The reader has seen the plan-coverage dialog. The BACKEND decides what an episode is, so
    // the client's whole part is saying "shown" on close.
    acknowledgePlanSlotNotice: {
      mutationFn: () => client.trackedTickersAcknowledgePlanSlotNotice(undefined),
    },
  };
}
